/**
 * Wire types do explorador de auditoria (A5 revamp · Brief 5 FE Tela 6).
 *
 * Triangulados campo-a-campo (R15) contra o serializer REAL do `AuditService`
 * (`apps/engine/src/modules/audit/audit.service.ts` → `SerializedAuditEvent`).
 *
 * NÃO confundir com os tipos drifted de `dpo/centro-direitos/types.ts`
 * (`AuditEventWireItem`/`AuditCascadeResponse`) que usam `createdAt`/`correlationId`/
 * shape de cascade divergente — aqueles não batem com o backend atual e ficam
 * intocados (não regredir B1).
 */

/** Severidade canônica do AuditEvent (enum `AuditSeverity` do Prisma). */
export type AuditSeverity = 'debug' | 'info' | 'warning' | 'high' | 'forensic';

export const AUDIT_SEVERITIES: readonly AuditSeverity[] = [
  'debug',
  'info',
  'warning',
  'high',
  'forensic',
];

/**
 * Evento serializado — exatamente os 10 campos que o backend devolve.
 * `payload` vira `{ _masked: true }` e `hasSensitive` vira `true` quando o caller
 * não tem `audit-events-read-sensitive` e a severidade é sensível (high/forensic).
 */
export interface AuditEventWire {
  id: string;
  tenantId: string;
  occurredAt: string;
  actorUserId: string | null;
  /** Nome do ator resolvido pelo Engine (batch); null = sistema ou não resolvido. */
  actorName?: string | null;
  eventType: string;
  entityType: string;
  entityId: string;
  severity: AuditSeverity;
  payload: Record<string, unknown>;
  hasSensitive: boolean;
}

/** Resposta de `GET /v1/audit-events` (cursor pagination). */
export interface AuditListResultWire {
  items: AuditEventWire[];
  nextCursor: string | null;
}

/** Resposta de `GET /v1/audit-events/cascade/:entityId`. */
export interface AuditCascadeResultWire {
  root: AuditEventWire;
  children: AuditEventWire[];
}

/** Bucket agregado do `GET /v1/audit-events/timeline`. */
export interface AuditTimelineBucketWire {
  periodStart: string;
  count: number;
  bySeverity: Record<string, number>;
}

/** Resposta de `GET /v1/audit-events/timeline`. */
export interface AuditTimelineResultWire {
  bucket: 'day' | 'hour';
  range: { since: string; until: string };
  total: number;
  buckets: AuditTimelineBucketWire[];
}

/** Filtros aceitos pelo `ListAuditEventsQuery` real (params derivados incluídos). */
export interface AuditListFilters {
  eventType?: string;
  actor?: string;
  since?: string;
  until?: string;
  entityType?: string;
  entityId?: string;
  severity?: AuditSeverity;
  category?: string;
  agent?: 'system' | 'user';
  cursor?: string;
  limit?: number;
}

export type AuditTimelineFilters = Omit<AuditListFilters, 'cursor' | 'limit'> & {
  bucket?: 'day' | 'hour';
};
